import React, { Fragment, useEffect, useState } from "react";
import { getAllJobs, getAllServices, corporateJobStatusUpdate, downloadPaymentVoucher } from "../../Redux/Actions/jobs";
import { apiWrapWithErrorWithData } from "../../utils/apiWrapper";
import DataTable from "../../utils/dataTable";
import { useSelector } from "react-redux";
import DropDown from "../Ui/DropDown";
import LoadingButton from "../../utils/loadingButton";
import { useTranslation } from "react-i18next";
import CustomModal from "../../utils/customModal";
import { toastMessage } from "../../utils/toast";
import { clearSession } from "../../utils/initiate";

function Jobs({ history }) {
  const userData = useSelector(({ User }) => User.data);
  const [jobs, setJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [processId, setProcessId] = useState("");
  const [masterProcessId, setMasterProcessId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [companyName, setCompanyName] = useState('');
  const [id, setId] = useState('');
  const [processList, setProcessList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [masterProcessList, setMasterProcessList] = useState([]);
  const [jobStatus, setJobStatus] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    getMasterProcessApi();
  }, []);

  useEffect(() => {
    getAllJobsApi(currentPage, companyName, departmentId?.value, masterProcessId?.value, processId?.value,id, jobStatus?.value);
  }, [currentPage]);

  const getAllJobsApi = async (page = 0, companyName = "", departmentId = "", masterProcessId = "", processId = "", id = 0, jobStatus = "") => {
    try {
      const res = await apiWrapWithErrorWithData(
        getAllJobs({
          companyName,
          departmentId,
          masterProcessId,
          processId,
          page,
          id,
          jobStatus,
        })
      );

      const response = res?.data;
      if (response?.data) {
        setJobs(response.data?.companyJobs);
        setTotalCount(response.data?.totalCount);
      }
      if(!res){
        toastMessage("error", "Session expired. Please login again.");
        history.push('/login');
      }
    } catch (error) {
      const status = error?.response?.status || error?.status;

      if (status === 401) {
        toastMessage("error", "Session expired. Please login again.");

        if (history && typeof history.push === "function") {
          setTimeout(() => {
            history.push("/login");
          }, 100);
        }
      } else {
        toastMessage("error", "Unable to fetch jobs");
        console.error("getAllJobsApi - Failed:", error);
      }
      console.error("getAllJobsApi Error:", {
        message: error.message,
        status: error?.response?.status,
        response: error?.response?.data,
        fullError: error,
      });
    }
  };

  const handleList = (key, ID) => {
    let list = [];
    if (key === "department") {
      list = departmentList.filter(({ value }) => value === ID)[0]
        .masterProcess;
      list = list.map(({ name, id, process }) => ({
        label: name,
        value: id,
        process: process,
      }));
      list.unshift({ label: "Select Department", value: 0, process: [] });
      setMasterProcessList(list);
      setProcessList([]);
      setMasterProcessId("");
      setProcessId("");
    } else {
      list = masterProcessList.filter(({ value }) => value === ID)[0].process;
      list = list.map(({ name, id }) => ({ label: name, value: id }));
      list.unshift({ label: "Select Master process", value: 0 });
      setProcessList(list);
    }
  };

  const getMasterProcessApi = async () => {
    const res = await apiWrapWithErrorWithData(getAllServices());
    if (!res?.data) return;
    const { data } = res?.data;
    let departmentsList = data?.departments || [];
    departmentsList = departmentsList.map(({ name, id, masterProcess }) => ({
      label: name,
      value: id,
      masterProcess: masterProcess,
    }));
    departmentsList.unshift({
      label: "Select Department",
      value: 0,
      masterProcess: [],
    });
    setDepartmentList(departmentsList);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    await getAllJobsApi(0, companyName, departmentId?.value, masterProcessId?.value, processId?.value,id, jobStatus.value);
    setIsLoading(false);
  }

  const handleClear = () => {
    setDepartmentId("")
    setMasterProcessId("")
    setProcessId("")
    setCompanyName("")
    setCurrentPage(0)
    setJobStatus("")  
    setId(0)
    getAllJobsApi()
  }

  const handleStatusUpdate = async () => {
    if (!selectedStatus || !selectedStatus.value) return;
    setModalLoading(true);
    try {
      await apiWrapWithErrorWithData(corporateJobStatusUpdate(selectedJobId, selectedStatus.value));
      toastMessage('success', 'Job status updated successfully');
      setModalOpen(false);
      setSelectedStatus(null);
      // Refetch jobs
      getAllJobsApi(currentPage, companyName, departmentId?.value, masterProcessId?.value, processId?.value, id, jobStatus?.value);
    } catch (error) {
      console.log("handleStatusUpdate", error);
      toastMessage('error', 'Failed to update job status');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDownloadVoucher = async (jobId) => {
    try {
      const response = await apiWrapWithErrorWithData(downloadPaymentVoucher(jobId));
      if(response.message) {
        console.log("handleDownloadVoucher - no voucher", response.message);
        toastMessage('error', 'No voucher available for download');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([response])) ?? null;
      const link = document.createElement('a');
      link.href = url;
      console.log("handleDownloadVoucher", url, link);
      link.setAttribute('download', `payment_voucher_${jobId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toastMessage('success', 'Payment voucher downloaded successfully');
    } catch (error) {
      console.log("handleDownloadVoucher", error);
      toastMessage('error', 'Failed to download payment voucher');
    }
  };

  const columns = [
    {
      Header: t("eidBasmati.id"),
      accessor: "id",
    },
    {
      Header: t("eidBasmati.corporateJobReference"),
      accessor: "corporateJobReference",
    },
    {
      Header: t("eidBasmati.poNumber"),
      accessor: "poNumber",
    },
    {
      Header: t("eidBasmati.companyName"),
      accessor: "companyName"
    },
    {
      Header: t('eidBasmati.jobInfo'),
      accessor: "departmentName",
      Cell: ({ row }) => (
        <div>
          <strong>D</strong> <strong>:</strong> {row.original.departmentName} <br />
          <strong>M</strong> <strong>:</strong> {row.original.masterProcessName}<br />
          <strong>P</strong> <strong>:</strong> {row.original.processName}<br />
          <strong>Job count</strong> <strong>:</strong> {row.original.jobCount}
        </div>
      )
    },
    {
      Header: t("eidBasmati.payment"),
      accessor: "paymentTypeValue",
      Cell: ({ row }) => (
        <div>
          <strong>Type</strong> <strong>:</strong> {row.original.paymentTypeValue} <br />
          <strong>Status</strong> <strong>:</strong> {row.original.paymentStatus}<br />
          <strong>Payment Date</strong> <strong>:</strong> {row.original.paymentCompletedDate}<br />
          <strong>Due Date</strong> <strong>:</strong> {row.original.dueDate}
        </div>
      )
    },
    {
      Header: t("eidBasmati.paymentApproval"),
      Cell: ({ cell }) => (
        <div>
          {cell.row.original.paymentTypeValue != "Online" && (
            <div>
              <strong>Status</strong> <strong>:</strong> {cell.row.original.pmtApprovalStatus}<br />
              <strong>Approved by</strong> <strong>:</strong> {cell.row.original.pmtApprovedBy}<br />
              <strong>Description</strong> <strong>:</strong> {cell.row.original.pmtApprovedNotes || '-'}
            </div>)}
        </div>
      )
    },
    {
      Header: t("eidBasmati.jobStatus"),
      accessor: "status",
    },
   
    {
      Header: t("eidBasmati.action"),
      accessor: "processId",
      Cell: ({ cell }) => (
        <div>
          {cell.row.original.isPayable == true && userData.Role === 'COMPANY' && (
            <button
              className="json-button btn btn-sm btn-primary"
              onClick={() => history.push(`/checkout/${cell.row.original.id}`)}
            >
              {t("eidBasmati.pay")}
            </button>
          )}

          {cell.row.original.id && (
            <button
              className="json-button btn btn-sm btn-primary m-2"
              onClick={() => history.push(`/corporate-jobs/${cell.row.original.id}`)}
            >
              {t("eidBasmati.view")}
            </button>
          )}

          {cell.row.original.id && cell.row.original.isEditable && (
            <button
              className="json-button btn btn-sm btn-primary m-2"
              onClick={() => history.push(`/corporate-jobs-edit/${cell.row.original.id}`)}
            >
              {t("eidBasmati.edit")}
            </button>
          )}

           {userData.Role === 'COMPANY' && cell.row.original.paymentStatus !== 'Completed' && (
            <button
              className="json-button btn btn-sm btn-warning m-2"
              onClick={() => {
                setSelectedJobId(cell.row.original.id);
                setSelectedStatus({ label: "Cancelled", value: 3 });
                setModalOpen(true);
              }}
            >
              {t("eidBasmati.updateStatus")}
            </button>
          )}

          {cell.row.original.id && userData.Role === 'COMPANY' && cell.row.original.paymentStatus == 'Completed' && (
            <button
              className="json-button btn btn-sm btn-info m-2"
              onClick={() => handleDownloadVoucher(cell.row.original.id)}
            >
              {t("eidBasmati.downloadVoucher")}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="d-flex">
        <div className="me-3" style={{ width: "100%" }}>
          <DropDown
            isSearchable={true}
            label={t("eidBasmati.department")}
            placeholder={t("eidBasmati.selectDepartment")}
            options={departmentList}
            value={departmentId}
            onChange={(value) => {
              setProcessList([]);
              handleList("department", value.value);
              setDepartmentId(value);
            }}
          />
        </div>
        <div className="me-3" style={{ width: "100%" }}>
          <DropDown
            isSearchable={true}
            label={t("eidBasmati.masterProcess")}
            placeholder={t("eidBasmati.selectMasterProcess")}
            options={masterProcessList}
            value={masterProcessId}
            onChange={(value) => {
              handleList("masterProcess", value.value);
              setMasterProcessId(value);
            }}
          />
        </div>
        <div className="me-3" style={{ width: "100%" }}>
          <DropDown
            isSearchable={true}
            label={t("eidBasmati.process")}
            placeholder={t("eidBasmati.selectProcess")}
            options={processList}
            value={processId}
            onChange={(value) => {
              setProcessId(value);
            }}
          />
        </div>
        <div className="me-3" style={{ width: "100%" }}>
          <DropDown
            isSearchable={true}
            label={t("eidBasmati.jobStatus")}
            placeholder={t("eidBasmati.selectJobStatus")}
            options={
              [
                { label: "Select Status", value: "" },
                { label: "Awaiting Payment", value: "Awaiting Payment" },
                { label: "Pending", value: "Pending" },
                { label: "Completed", value: "Completed" },
                { label: "Draft", value: "Draft" },
                { label: "Cancelled", value: "Cancelled" },
              ]
            }
            value={jobStatus}
            onChange={(value) => {
              setJobStatus(value);
            }}
          />
        </div>
      </div>
      <div className="d-flex">
        {userData.Role === "AFFILIATEAGENT" &&
          <div className="me-3" style={{ width: "32%" }}>
            <label
              className="form-label col-md-4 col-sm-2"
              style={{ marginTop: "5px" }}
            >
              Company
            </label>
            <input
              className="p-1 form-control col-md-4 col-sm-2"
              type="text"
              onChange={(e) => setCompanyName(e.target.value)}
              value={companyName}
              placeholder="Please enter company.."
            />
          </div>}
        <div className="me-3" style={{ width: "32%" }}>
          <label
            className="form-label col-md-4 col-sm-2"
            style={{ marginTop: "5px" }}
          >
            {t("eidBasmati.id")}
          </label>
          <input
            className="p-1 form-control col-md-4 col-sm-2"
            type="number"
            onChange={(e) => setId(e.target.value)}
            value={id}
            placeholder={t("eidBasmati.enterId")}
          />
        </div>
      </div>
      <div className="d-flex justify-content-start mt-3">
        <LoadingButton isLoading={isLoading} onChange={handleSubmit}>
          {t("eidBasmati.search")}
        </LoadingButton>
        <div className="mx-5">
          <LoadingButton onChange={handleClear}>
            {t("eidBasmati.clear")}
          </LoadingButton>
        </div>
      </div>
      <Fragment>
        {userData.Role === "COMPANY" ?
          <div className="d-flex justify-content-end">
            <button
              className="btn btn-primary m-3"
              onClick={() => history.push(`/jobs/create`)}
            >
              {t("eidBasmati.createJob")}
            </button>
            <button
              className="btn btn-primary m-3"
              onClick={() => history.push(`/job-eid-basmati-create`)}
            >
               {t("eidBasmati.eidBasmati")}
            </button>
          </div> : null}
        <DataTable
          columns={columns}
          currentPage={currentPage}
          data={jobs}
          setCurrentPage={setCurrentPage}
          totalCount={totalCount}
        />
      </Fragment>
      <CustomModal
        show={modalOpen}
        handleClose={() => setModalOpen(false)}
        title={t("eidBasmati.updateJobStatus")}
        body={
          <div>
            <p style={{ fontWeight: "bold", marginBottom: 16 }}>
              {"Are you sure you want to cancel this job?"}
            </p>
            <DropDown
              label={t("eidBasmati.selectJobStatus")}
              placeholder="Choose a status"
              options={[
                { label: "Cancelled", value: 3 },
              ]}
              value={selectedStatus}
              onChange={(value) => setSelectedStatus(value)}
              disabled={true}
            />
          </div>
        }
        isLoading={modalLoading}
        handleSubmit={handleStatusUpdate}
      />
    </div>
  );
}

export default Jobs;
